import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { fetchSavedSearches, deleteSavedSearch } from '../api';
import { useWorkflow } from '../context/WorkflowContext';

const { FiBookmark, FiTrash2, FiArrowRight, FiLoader, FiSearch, FiAlertCircle, FiCalendar } = FiIcons;

const SavedReportsPage = ({ user }) => {
  const { setMarkdownReport, setCompanyName, setLocation, setPhase } = useWorkflow();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const data = await fetchSavedSearches(user);
      setReports(data);
    } catch (err) {
      setError('Failed to load saved reports.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = (report) => {
    setMarkdownReport(report.markdown_report);
    setCompanyName(report.customer_name);
    setLocation(report.country);
    setPhase(3);
  };

  const handleDelete = async (id) => {
    setDeleting(id);
    try {
      await deleteSavedSearch(id);
      setReports((prev) => prev.filter((r) => r.id !== id));
    } catch {
      setError('Failed to delete report.');
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-10">
      <div className="mb-10">
        <div className="inline-flex items-center gap-2 bg-sonar-green/10 text-sonar-green px-3 py-1 rounded-full text-[10px] font-bold border border-sonar-green/30 tracking-widest uppercase mb-4">
          <SafeIcon icon={FiBookmark} /> Cloud Archive
        </div>
        <h1 className="text-3xl font-black text-white uppercase tracking-tight">
          Saved <span className="text-sonar-green">Reports</span>
        </h1>
        <p className="text-slate-500 font-mono text-xs mt-1 uppercase tracking-widest">
          {reports.length} report{reports.length !== 1 ? 's' : ''} stored in your account
        </p>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-20">
          <SafeIcon icon={FiLoader} className="text-sonar-green text-3xl animate-spin" />
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-500/10 border border-red-500/30 rounded-xl mb-6">
          <SafeIcon icon={FiAlertCircle} className="text-red-400" />
          <p className="text-sm text-red-400 font-mono">{error}</p>
        </div>
      )}

      {!loading && reports.length === 0 && (
        <div className="text-center py-20 border border-dashed border-sonar-border rounded-2xl">
          <SafeIcon icon={FiSearch} className="text-4xl text-slate-600 mx-auto mb-4" />
          <p className="text-slate-500 font-mono text-sm">No saved reports yet.</p>
          <p className="text-slate-600 font-mono text-xs mt-1">Generate a credit memo and it will be auto-saved here.</p>
        </div>
      )}

      <div className="space-y-4">
        {reports.map((report, i) => (
          <motion.div
            key={report.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05 }}
            className="bg-sonar-card border border-sonar-border rounded-2xl p-6 flex items-center justify-between gap-4 hover:border-sonar-green/50 transition-all group"
          >
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-white text-base truncate">{report.customer_name}</h3>
              <div className="flex items-center gap-3 mt-1 flex-wrap">
                {report.country && (
                  <span className="text-[10px] font-mono text-slate-500 uppercase tracking-widest">
                    {report.country}
                  </span>
                )}
                <span className="flex items-center gap-1 text-[10px] font-mono text-slate-600">
                  <SafeIcon icon={FiCalendar} className="text-[9px]" />
                  {new Date(report.created_at).toLocaleDateString()}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => handleDelete(report.id)}
                disabled={deleting === report.id}
                className="p-2 text-slate-600 hover:text-red-400 transition-colors"
              >
                {deleting === report.id
                  ? <SafeIcon icon={FiLoader} className="animate-spin text-sm" />
                  : <SafeIcon icon={FiTrash2} className="text-sm" />
                }
              </button>
              <button
                onClick={() => handleOpen(report)}
                className="flex items-center gap-2 px-4 py-2 bg-sonar-green/10 text-sonar-green border border-sonar-green/30 rounded-xl text-xs font-bold uppercase tracking-widest hover:bg-sonar-green hover:text-black transition-all"
              >
                Open <SafeIcon icon={FiArrowRight} />
              </button>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default SavedReportsPage;